import { useState } from 'react'

import Reveal from '@/components/motion/Reveal'
import Change from '@/components/ui/Change'
import Money from '@/components/ui/Money'
import SectionHead from '@/components/ui/SectionHead'
import { TOUCH_TARGET_Y } from '@/components/ui/button-variants'
import SymbolLink from '@/components/ui/SymbolLink'
import { toast } from '@/components/ui/toast-store'
import RailRows from '@/features/market-rail/components/RailRows'
import { useRailBlock } from '@/features/market-rail/useRailFit'
import { useRemoveWatchlistSymbol } from '@/features/watchlist/api/mutations'
import { useWatchlistCollection } from '@/features/watchlist/api/queries'
import { cn } from '@/lib/utils'
import type { WatchRow } from '@/schemas/watchlist'

const RAIL_WATCHLIST_ROWS = 5

interface WatchlistRowProps {
  row: WatchRow
  /** Edit mode: "Remove" stands where the change figure does. */
  editing: boolean
  /** Fading out while its DELETE is in flight. */
  leaving: boolean
  onRemove: (symbol: string) => void
}

/**
 * One watchlist row in the rail: symbol, last price, change — or "Remove" in the change's own
 * column while the block is being edited. The mock's row (`hm-wl__row`): a plain row with no
 * wash, its ink going from the secondary grey to the foreground on hover, a hairline between rows
 * (under all but the last, which `RailRows`'s parked sentinel keeps true for the fit), price and
 * change in fixed right-aligned columns.
 *
 * Leaving is a transition, not an entrance: the row fades and slides 12px out and gives its
 * height back (`max-h-0`), then the refetch the delete triggers takes it out of the list.
 */
function WatchlistRow({ row, editing, leaving, onRemove }: WatchlistRowProps) {
  return (
    <div
      className={cn(
        'relative flex max-h-10 items-center gap-2 border-b border-border-subtle py-2 text-body-m text-muted-foreground transition-[color,opacity,translate,max-height,padding] duration-med ease-out last:border-b-0 hover:text-foreground focus-within:text-foreground',
        leaving && 'pointer-events-none max-h-0 translate-x-3 overflow-hidden py-0 opacity-0',
      )}
    >
      {/* The row inks up on hover, so the whole row is the symbol's link (as Popular stocks' is),
          except while editing, when "Remove" must take its own clicks. */}
      <SymbolLink
        symbol={row.symbol}
        className={cn(
          'min-w-0 flex-1 truncate font-semibold',
          !editing && "after:absolute after:inset-0 after:content-['']",
        )}
      />
      <Money value={row.ltp} className="w-20 text-right" />
      {editing ? (
        <Reveal variant="fade-in" className="w-18 text-right">
          <button
            type="button"
            aria-label={`Remove ${row.symbol}`}
            onClick={() => {
              onRemove(row.symbol)
            }}
            className="cursor-pointer text-body-xs text-muted-foreground-subtle transition-colors duration-fast ease-out hover:text-destructive focus-visible:text-destructive focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            Remove
          </button>
        </Reveal>
      ) : (
        <Change value={row.percentChange} percent className="w-18 justify-end" />
      )}
    </div>
  )
}

/**
 * Home · Personalised's own rail block (`rail/watchlist`): the watchlist's first non-empty list,
 * five rows, symbol · last price · change.
 *
 * "Edit" is the mock's inline edit (owner ruling 7, 2026-10-28), not a link out to `/watchlist`:
 * it turns into "Done", and each row swaps its change figure for "Remove" — the existing
 * `DELETE /watchlists/{group_id}/stocks/{symbol}/`. Done leaves edit mode; the rows already
 * removed stay removed. A failed delete puts the row back and says so.
 *
 * Reads the same `useWatchlistCollection()` cache entry `HomeScreen`'s own gate and
 * `EmptyWatchlistNudge` read — one request, three observers — and renders nothing when the list is
 * empty (the empty state lives in the main column instead, `EmptyWatchlistNudge`) or hasn't
 * resolved yet (a loading rail block flashing in above a settled Popular stocks would be a worse
 * first paint than one block landing a beat later).
 */
export default function HomeWatchlistRailBlock() {
  const block = useRailBlock('watchlist')
  const collection = useWatchlistCollection()
  const remove = useRemoveWatchlistSymbol()
  const [editing, setEditing] = useState(false)
  const [leaving, setLeaving] = useState<string | null>(null)

  const list = collection.data?.lists.find((entry) => entry.rows.length > 0)
  const rows = list?.rows.slice(0, RAIL_WATCHLIST_ROWS) ?? []

  // A list emptied by its last Remove leaves the block with nothing to edit: it goes, and so does
  // the edit mode it was in.
  if (list === undefined || rows.length === 0) return null

  const onRemove = (symbol: string) => {
    setLeaving(symbol)
    remove.mutate(
      { id: list.id, symbol },
      {
        onError: () => {
          toast.error(`Couldn't remove ${symbol}. Try again.`)
        },
        onSettled: () => {
          setLeaving(null)
        },
      },
    )
  }

  return (
    <section aria-label="Your watchlist" {...block}>
      <SectionHead
        size="sm"
        title="Your watchlist"
        className="pb-2"
        action={
          <button
            type="button"
            onClick={() => {
              setEditing((current) => !current)
            }}
            className={cn(
              'relative cursor-pointer text-label-l text-link hover:underline focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
              TOUCH_TARGET_Y,
            )}
          >
            {editing ? 'Done' : 'Edit'}
          </button>
        }
      />
      <RailRows
        block="watchlist"
        rows={rows.map((row) => (
          <WatchlistRow
            key={row.symbol}
            row={row}
            editing={editing}
            leaving={leaving === row.symbol}
            onRemove={onRemove}
          />
        ))}
      />
    </section>
  )
}
